/**
 * Keyboard-Help-Overlay — full shortcut reference.
 *
 * Singleton: one help overlay at a time on the page. Toggled via openHelp()
 * (called by the player's keyboard handler on `?` and the toolbar's help
 * button). Listens in capture-phase for Esc/? to ensure it closes BEFORE the
 * modal's own Esc-handler triggers — otherwise hitting Esc to dismiss help
 * would also tear down the player.
 *
 * Inserted on document.body so the overlay survives independent of any player
 * mode (modal or inline).
 */

const HELP_HTML = `
  <div class="zap-help-overlay__backdrop" data-act="close"></div>
  <div class="zap-help-overlay__panel" role="dialog" aria-modal="true" aria-label="Tastatur-Shortcuts">
    <header class="zap-help-overlay__h">
      <h3>Tastatur-Shortcuts</h3>
      <button class="zap-help-overlay__close" data-act="close" aria-label="Schließen">×</button>
    </header>
    <div class="zap-help-grid">
      <section class="zap-help-col">
        <div class="zap-help-col__h">Transport</div>
        <div class="zap-help-row"><span><kbd>Space</kbd>/<kbd>K</kbd></span><span>Play / Pause</span></div>
        <div class="zap-help-row"><span><kbd>F</kbd></span><span>Vollbild</span></div>
        <div class="zap-help-row"><span><kbd>M</kbd></span><span>Stumm</span></div>
        <div class="zap-help-row"><span><kbd>↑</kbd>/<kbd>↓</kbd></span><span>±5 % Lautstärke</span></div>
        <div class="zap-help-row"><span><kbd>←</kbd>/<kbd>→</kbd> · <kbd>J</kbd>/<kbd>L</kbd></span><span>±Step (kleiner bei Pause)</span></div>
        <div class="zap-help-row"><span><kbd>,</kbd>/<kbd>.</kbd> · <kbd>&lt;</kbd>/<kbd>&gt;</kbd></span><span>Speed ±0,25×</span></div>
        <div class="zap-help-row"><span><kbd>0</kbd>–<kbd>9</kbd></span><span>Position N×10 %</span></div>
        <div class="zap-help-row"><span><kbd>P</kbd></span><span>Picture-in-Picture</span></div>
        <div class="zap-help-row"><span><kbd>S</kbd></span><span>Stream-Stats Overlay</span></div>
        <div class="zap-help-row"><span><kbd>Esc</kbd></span><span>Modal / Vollbild schließen</span></div>
      </section>
      <section class="zap-help-col">
        <div class="zap-help-col__h">Editor</div>
        <div class="zap-help-row"><span><kbd>Shift</kbd>+<kbd>←</kbd>/<kbd>→</kbd></span><span>Frame-Step (1/fps)</span></div>
        <div class="zap-help-row"><span><kbd>+</kbd>/<kbd>−</kbd></span><span>Timeline-Zoom</span></div>
        <div class="zap-help-row"><span><span class="zap-help-mouse">Rechtsklick</span> Region</span><span>Modus-Menü</span></div>
        <div class="zap-help-row"><span><span class="zap-help-mouse">Drag</span> Handle</span><span>Region resize</span></div>
        <div class="zap-help-row"><span><span class="zap-help-mouse">Wheel</span> Strip</span><span>Zoom</span></div>
        <div class="zap-help-row"><span><kbd>Shift</kbd>+<span class="zap-help-mouse">Wheel</span></span><span>Pan</span></div>
      </section>
      <section class="zap-help-col">
        <div class="zap-help-col__h">A/B-Loop &amp; Regions</div>
        <div class="zap-help-row"><span><kbd>B</kbd></span><span>Marker setzen (Maus über Strip oder Cursor)</span></div>
        <div class="zap-help-row"><span><kbd>Space</kbd> nach B</span><span>Loop A→B starten</span></div>
        <div class="zap-help-row"><span><kbd>B</kbd> oder <kbd>Space</kbd></span><span>laufenden Loop beenden</span></div>
        <div class="zap-help-row"><span><span class="zap-help-mouse">Klick</span> 🔁-Button</span><span>Loop als Region speichern</span></div>
        <div class="zap-help-row"><span><kbd>?</kbd></span><span>diese Hilfe an/aus</span></div>
      </section>
    </div>
    <section class="zap-help-explainer">
      <h4>Wie der Player funktioniert</h4>
      <p><strong>Direct-Play</strong> — wenn die Datei H.264 + AAC + moov-vorne ist (typisch bei .mp4),
        spielt der Browser die Datei direkt mit Range-Requests. Kein FFmpeg, kein Transcode,
        Cold-Start unter 1 s. Du erkennst das im Info-Panel unter dem Player („Quelle: Direct-Play").</p>
      <p><strong>HLS-Transcode</strong> — für HEVC/VC-1/4K-Quellen oder mit Audio-Norm an, läuft
        FFmpeg im Hintergrund und produziert MPEG-TS-Segmente in den RAM (tmpfs). Die ersten
        Segmente kommen nach ~3-5 s; bei 4K-HEVC ohne GPU sind 5-12 s realistisch.</p>
      <p><strong>Seek</strong> — kleine Sprünge (&lt; 30 s) nutzen den vorhandenen Encoder. Größere
        Sprünge killen FFmpeg und starten ihn an der neuen Position neu; der Player wird kurz
        neu attached, was 2-5 s Pause erzeugt aber Stalls verhindert.</p>
      <p><strong>Qualität</strong> — Default „auto": kopiert H.264 (cheap), transcodiert anderes
        auf 720p, 4K-HEVC auf 480p (sonst nicht real-time auf CPU). Per Toolbar-Zahnrad
        manuell auf 720p / 1080p / max forcieren.</p>
      <p><strong>Vollbild</strong> — der native Browser-Button im Video-Player macht echtes
        Browser-Vollbild (nur das Video). Die <kbd>F</kbd>-Taste macht Editor-Vollbild —
        Strip, Banner und Region-Tools bleiben sichtbar.</p>
      <p><strong>A/B-Loop</strong> — <kbd>B</kbd> setzt einen Marker an der aktuellen Position
        (oder der Maus-Position, falls du gerade über der Timeline-Strip bist). Ein zweiter
        <kbd>B</kbd>+<kbd>Space</kbd> startet einen Loop zwischen den Markern. Speichern
        (Region) macht ihn permanent.</p>
    </section>
    <footer class="zap-help-overlay__f">
      <span class="dim mono">Region-Modes: skip · include · repeat · reverse · fwd-back-fwd · per-region speed override</span>
    </footer>
  </div>`;

/**
 * Toggle the help overlay. Call again to close.
 * @returns {boolean} true if opened, false if closed
 */
export function openHelp() {
  const existing = document.getElementById('zap-help-overlay');
  if (existing) {
    existing._zapClose?.();
    return false;
  }

  const el = document.createElement('div');
  el.id = 'zap-help-overlay';
  el.className = 'zap-help-overlay';
  el.innerHTML = HELP_HTML;
  document.body.appendChild(el);

  let onKey;
  const close = () => {
    if (onKey) document.removeEventListener('keydown', onKey, true);
    el.remove();
  };
  el._zapClose = close;

  el.addEventListener('click', (e) => {
    if (e.target.closest('[data-act="close"]')) close();
  });

  onKey = (e) => {
    const k = e.key;
    if (k === 'Escape' || k === '?' || (e.shiftKey && k === '/')) {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };
  // capture-phase so we close BEFORE the modal's bubble-phase Esc-handler
  // can teardown the player.
  document.addEventListener('keydown', onKey, true);

  return true;
}

export function closeHelp() {
  document.getElementById('zap-help-overlay')?._zapClose?.();
}

export function isHelpOpen() {
  return !!document.getElementById('zap-help-overlay');
}
